import React, { useEffect, useMemo, useRef, useState } from 'react';

type Option = { id: string; label: string };

export default function SearchableSelect(props: {
  valueId: string;
  query?: string;
  placeholder?: string;
  options: Option[];
  disabled?: boolean;
  showIdInMenu?: boolean;
  autoFocus?: boolean;
  onQueryChange?: (q: string) => void;
  onSelectId: (id: string) => void;
  onResetQueryToValue?: () => void;
  renderTrigger?: (triggerProps: { onClick: () => void; disabled?: boolean }) => React.ReactNode;
}): React.ReactElement {
  const {
    valueId,
    placeholder,
    options,
    disabled,
    showIdInMenu = true,
    onQueryChange,
    onSelectId,
    onResetQueryToValue,
    renderTrigger,
  } = props;
  const controlledQuery = typeof props.query === 'string';
  const [uncontrolledQuery, setUncontrolledQuery] = useState('');
  const query = controlledQuery ? (props.query as string) : uncontrolledQuery;
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuIdRef = useRef(`ops-searchable-menu-${Math.random().toString(36).slice(2)}`);

  const filtered = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return options.slice(0, 60);
    return options
      .filter(o => o.label.toLowerCase().includes(needle) || o.id.toLowerCase().includes(needle))
      .slice(0, 60);
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const t = e.target as any;
      if (t && root.contains(t)) return;
      setOpen(false);
      setActiveIdx(-1);
      onResetQueryToValue?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onResetQueryToValue]);

  // Keep uncontrolled query aligned with selection when not actively searching.
  useEffect(() => {
    if (controlledQuery) return;
    if (open) return;
    const opt = options.find(o => String(o.id) === String(valueId));
    setUncontrolledQuery(opt ? opt.label : '');
  }, [controlledQuery, open, options, valueId]);

  const commitSelect = (id: string) => {
    onSelectId(id);
    setOpen(false);
    setActiveIdx(-1);
    if (!controlledQuery) {
      const opt = options.find(o => String(o.id) === String(id));
      setUncontrolledQuery(opt ? opt.label : '');
    }
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!props.autoFocus) return;
    // Focus after mount so parent layouts are stable.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [props.autoFocus]);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {renderTrigger ? (
        renderTrigger({
          onClick: () => {
            if (disabled) return;
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          },
          disabled,
        })
      ) : null}
      <input
        ref={inputRef}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (controlledQuery) onQueryChange?.(next);
          else setUncontrolledQuery(next);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          const next = (e.relatedTarget as any) || null;
          const root = rootRef.current;
          if (root && next && root.contains(next)) return;
          setOpen(false);
          setActiveIdx(-1);
          onResetQueryToValue?.();
          if (!controlledQuery) {
            const opt = options.find(o => String(o.id) === String(valueId));
            setUncontrolledQuery(opt ? opt.label : '');
          }
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
          }
          if (e.key === 'Escape') {
            setOpen(false);
            setActiveIdx(-1);
            onResetQueryToValue?.();
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIdx(prev => Math.min(filtered.length - 1, (prev < 0 ? 0 : prev + 1)));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(prev => Math.max(0, (prev < 0 ? 0 : prev - 1)));
            return;
          }
          if (e.key === 'Enter') {
            if (!open) return;
            e.preventDefault();
            const idx = activeIdx >= 0 ? activeIdx : 0;
            const opt = filtered[idx];
            if (opt) commitSelect(opt.id);
          }
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuIdRef.current}
        aria-autocomplete="list"
        style={renderTrigger ? { width: '100%', display: open ? 'block' : 'none' } : { width: '100%' }}
      />

      {open && filtered.length > 0 && (
        <div
          id={menuIdRef.current}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 10,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            maxHeight: 240,
            overflow: 'auto',
            boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.map((opt, idx) => {
            const isActive = idx === activeIdx;
            const isSelected = String(valueId || '') === String(opt.id || '');
            return (
              <button
                key={opt.id}
                type="button"
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => commitSelect(opt.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background: isActive ? '#f3f4f6' : (isSelected ? '#eef2ff' : 'white'),
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, color: '#111827' }}>{opt.label}</div>
                {showIdInMenu && (
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{opt.id}</div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

